/**
 * EntityDetailDrawer — one Sheet-style drawer that turns any Contracts row
 * (Claim / LD Tracker / Variation / Dispute / Correspondence) into a fully
 * editable detail view with a status ribbon, supporting-docs uploader and an
 * inline chat/comment thread.  Kept as a single component so the 5 tabs share
 * consistent UX, testids and status semantics.
 */
import { useEffect, useMemo, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { Save, Send, FileText, Paperclip, Clock, User, ExternalLink } from 'lucide-react';
import api from '@/lib/api';
import { SupportingDocsPicker } from './SupportingDocsPicker';
import { openContractDoc } from '@/utils/contractDocUrl';

// Status vocabularies mirror the backend enums (contracts_models.py) — a ribbon
// step that the API will not accept is worse than no ribbon at all.
const STATUS_TRACKS = {
  claim:         ['DRAFT','AI_GENERATED','SUBMITTED','ACKNOWLEDGED','UNDER_REVIEW','NEGOTIATION','PARTIALLY_APPROVED','APPROVED','REJECTED','WITHDRAWN'],
  ld:            ['MONITORING','TRIGGERED','WAIVER_REQUESTED','WAIVED','APPLIED','DISPUTED'],
  variation:     ['DRAFT','SUBMITTED','UNDER_REVIEW','APPROVED','IMPLEMENTED','REJECTED','WITHDRAWN'],
  dispute:       ['RAISED','NEGOTIATION','MEDIATION','ADJUDICATION','ARBITRATION','LITIGATION','RESOLVED','SETTLED','WITHDRAWN'],
  correspondence:['DRAFT','SENT','ACKNOWLEDGED','RESPONDED','CLOSED'],
};

// Statuses that end or branch off the track — reaching one of these does not
// mean every earlier step was passed, so nothing behind them is marked done.
const TERMINAL_STATUSES = new Set(['REJECTED', 'WITHDRAWN', 'PARTIALLY_APPROVED', 'SUPERSEDED', 'DISPUTED']);

// Field schema per entity type: [key, label, type, options?]
const FIELDS = {
  claim: [
    ['title','Title','text'], ['description','Description','textarea'],
    ['claim_type','Type','select',['EOT','VARIATION','DELAY_DAMAGES','DISRUPTION','ACCELERATION','FORCE_MAJEURE','PROLONGATION']],
    ['event_date','Event Date','date'], ['requested_days','Requested Days','number'],
    ['currency','Currency','select',['INR','USD','EUR','GBP','AED','SAR','SGD','JPY']],
    ['requested_amount','Requested Amount','number'],
    ['approved_amount','Approved Amount','number'], ['notice_date','Notice Date','date'],
    ['notice_reference','Notice Reference','text'], ['supporting_clause','Supporting Clause','text'],
  ],
  ld: [
    ['milestone_name','Milestone Name','text'],
    ['ld_type','LD Type','select',['OVERALL_COMPLETION','MILESTONE','SECTIONAL','PERFORMANCE']],
    ['reference','Reference','text'], ['contractual_date','Contractual Date','date'],
    ['currency','Currency','select',['INR','USD','EUR','GBP','AED','SAR','SGD','JPY']],
    ['daily_rate','Daily Rate','number'], ['cap_amount','Cap Amount','number'],
    ['cap_percent_of_value','Cap %','number'], ['grace_period_days','Grace Days','number'],
    ['grace_clause','Grace Clause','text'],
    ['calculation_basis','Calculation Basis','select',['CONTRACT_VALUE','MILESTONE_VALUE','SECTION_VALUE','DAILY_FIXED']],
    ['notification_recipient','Notification Recipient','select',['CONTRACTOR','EMPLOYER','ENGINEER']],
    ['notes','Notes','textarea'],
  ],
  variation: [
    ['title','Title','text'], ['description','Description','textarea'],
    ['variation_type','Type','select',['ADDITION','OMISSION','SUBSTITUTION','DESIGN_CHANGE','SCOPE_CHANGE','ACCELERATION']],
    ['currency','Currency','select',['INR','USD','EUR','GBP','AED','SAR','SGD','JPY']],
    ['requested_amount','Amount','number'], ['approved_amount','Approved','number'],
    ['requested_days','Days','number'], ['reference','Reference','text'],
    ['origin_date','Origin Date','date'],
    ['originator','Originator','select',['CONTRACTOR','EMPLOYER','OWNER','ENGINEER','CONSULTANT']],
    ['priority','Priority','select',['LOW','MEDIUM','HIGH','CRITICAL']],
    ['supporting_clause','Supporting Clause','text'],
  ],
  dispute: [
    ['title','Title','text'], ['description','Description','textarea'],
    ['dispute_type','Type','select',['PAYMENT','VARIATION_VALUE','EOT_ENTITLEMENT','LD_CHALLENGE','QUALITY','SCOPE','TERMINATION','OTHER']],
    ['currency','Currency','select',['INR','USD','EUR','GBP','AED','SAR','SGD','JPY']],
    ['amount_in_dispute','Amount','number'], ['resolution_amount','Resolution Amount','number'],
    ['raised_by','Raised By','select',['CONTRACTOR','EMPLOYER']],
    ['notice_reference','Notice Reference','text'], ['notified_on','Notified On','date'],
    ['escalation_stage','Escalation Stage','select',['CORRESPONDENCE','AMICABLE','MEDIATION','DAB','ARBITRATION','LITIGATION']],
    ['governing_clause','Governing Clause','text'],
    ['desired_outcome','Desired Outcome','textarea'],
  ],
  correspondence: [
    ['subject','Subject','text'], ['content','Content','textarea'],
    ['correspondence_type','Type','select',['NOTICE','LETTER','RFI','SITE_INSTRUCTION','DIRECTION','RESPONSE','CLAIM_NOTICE','EOT_NOTICE','LD_NOTICE','GENERAL']],
    ['from_party','From','select',['CONTRACTOR','EMPLOYER','SUBCONTRACTOR','ENGINEER']],
    ['to_party','To','select',['EMPLOYER','CONTRACTOR','ENGINEER','SUBCONTRACTOR']],
    ['reference_number','Reference No.','text'], ['sent_date','Sent Date','date'],
    ['priority','Priority','select',['LOW','MEDIUM','HIGH','URGENT']],
    ['linked_clause','Linked Clause','text'],
  ],
};

const STATUS_COLORS = {
  DRAFT:'bg-slate-500/20 text-slate-400', NOTICE_ISSUED:'bg-blue-500/20 text-blue-400',
  ASSESSED:'bg-purple-500/20 text-purple-400', NEGOTIATED:'bg-indigo-500/20 text-indigo-400',
  SETTLED:'bg-emerald-500/20 text-emerald-400', REJECTED:'bg-red-500/20 text-red-400',
  WITHDRAWN:'bg-slate-500/20 text-slate-400',
  MONITORING:'bg-blue-500/20 text-blue-400', TRIGGERED:'bg-amber-500/20 text-amber-400',
  WAIVER_REQUESTED:'bg-purple-500/20 text-purple-400', WAIVED:'bg-emerald-500/20 text-emerald-400',
  APPLIED:'bg-red-500/20 text-red-400', DISPUTED:'bg-red-500/20 text-red-400',
  ISSUED:'bg-blue-500/20 text-blue-400', UNDER_REVIEW:'bg-purple-500/20 text-purple-400',
  APPROVED:'bg-emerald-500/20 text-emerald-400', SUPERSEDED:'bg-slate-500/20 text-slate-400',
  OPEN:'bg-red-500/20 text-red-400', IN_MEDIATION:'bg-amber-500/20 text-amber-400',
  IN_DAB:'bg-purple-500/20 text-purple-400', IN_ARBITRATION:'bg-indigo-500/20 text-indigo-400',
  RESOLVED:'bg-emerald-500/20 text-emerald-400', CLOSED:'bg-slate-500/20 text-slate-400',
  SENT:'bg-blue-500/20 text-blue-400', ACKNOWLEDGED:'bg-purple-500/20 text-purple-400',
  RESPONDED:'bg-emerald-500/20 text-emerald-400',
  AI_GENERATED:'bg-purple-500/20 text-purple-400', SUBMITTED:'bg-blue-500/20 text-blue-400',
  NEGOTIATION:'bg-indigo-500/20 text-indigo-400', PARTIALLY_APPROVED:'bg-teal-500/20 text-teal-400',
  IMPLEMENTED:'bg-emerald-500/20 text-emerald-400', RAISED:'bg-red-500/20 text-red-400',
  MEDIATION:'bg-amber-500/20 text-amber-400', ADJUDICATION:'bg-purple-500/20 text-purple-400',
  ARBITRATION:'bg-indigo-500/20 text-indigo-400', LITIGATION:'bg-red-500/20 text-red-400',
};

const API_MAP = {
  claim:          (cid, id) => ({ get: `/contracts/contracts/${cid}/claims/${id}`,          patch: `/contracts/contracts/${cid}/claims/${id}`,          attach: `/contracts/entities/claims/${id}/attach`,          messages: `/contracts/entities/claims/${id}/messages` }),
  ld:             (cid, id) => ({ get: `/contracts/contracts/${cid}/ld-trackers/${id}`,      patch: `/contracts/contracts/${cid}/ld-trackers/${id}`,      attach: `/contracts/entities/ld-trackers/${id}/attach`,      messages: `/contracts/entities/ld-trackers/${id}/messages` }),
  variation:      (cid, id) => ({ get: `/clm/contracts/${cid}/variations/${id}`,             patch: `/clm/contracts/${cid}/variations/${id}`,             attach: `/contracts/entities/variations/${id}/attach`,       messages: `/contracts/entities/variations/${id}/messages` }),
  dispute:        (cid, id) => ({ get: `/clm/contracts/${cid}/disputes/${id}`,               patch: `/clm/contracts/${cid}/disputes/${id}`,               attach: `/contracts/entities/disputes/${id}/attach`,         messages: `/contracts/entities/disputes/${id}/messages` }),
  correspondence: (cid, id) => ({ get: `/clm/contracts/${cid}/correspondences/${id}`,        patch: `/clm/contracts/${cid}/correspondences/${id}`,        attach: `/contracts/entities/correspondences/${id}/attach`,  messages: `/contracts/entities/correspondences/${id}/messages` }),
};

const API_URL = process.env.REACT_APP_BACKEND_URL; // eslint-disable-line no-unused-vars

export function EntityDetailDrawer({ open, onOpenChange, entityType, entity, contractId, onSaved }) {
  const [form, setForm] = useState(entity || {});
  const [saving, setSaving] = useState(false);
  const [files, setFiles] = useState([]);
  const [messages, setMessages] = useState([]);
  const [msgInput, setMsgInput] = useState('');
  useEffect(() => { setForm(entity || {}); setFiles([]); setMessages([]); }, [entity]);

  const eps = useMemo(() => (entity?.id && entityType) ? API_MAP[entityType](contractId, entity.id) : null, [entity, entityType, contractId]);
  const track = STATUS_TRACKS[entityType] || [];
  const fields = FIELDS[entityType] || [];
  const currentStatus = form.status || track[0] || '—';

  useEffect(() => {
    if (!open || !eps) return;
    (async () => { try { const r = await api.get(eps.messages); setMessages(r.data?.messages || []); } catch { setMessages([]); } })();
  }, [open, eps]);

  const dateVal = (v) => v ? String(v).slice(0,10) : '';
  const toIso = (v) => v ? new Date(v).toISOString() : null;

  const save = async () => {
    if (!eps) return;
    setSaving(true);
    try {
      const payload = { ...form };
      fields.forEach(([k,,type]) => {
        if (type === 'date' && payload[k]) payload[k] = toIso(payload[k]);
        if (type === 'number' && payload[k] !== '' && payload[k] !== null && payload[k] !== undefined) payload[k] = Number(payload[k]);
      });
      const r = await api.patch(eps.patch, payload);
      if (files.length) {
        const fd = new FormData(); files.forEach(f => fd.append('files', f));
        await api.post(eps.attach, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      toast.success('Saved');
      onSaved?.(r.data);
      setFiles([]);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const transition = async (nextStatus) => {
    if (nextStatus === currentStatus) return;
    // One stray click used to overwrite a live status, so it is confirmed first.
    if (!window.confirm(`Move this ${entityType} from ${currentStatus} to ${nextStatus.replace(/_/g, ' ')}?`)) return;
    const previous = currentStatus;
    setForm(f => ({ ...f, status: nextStatus }));
    try {
      await api.patch(eps.patch, { status: nextStatus });
      toast.success(`Moved to ${nextStatus}`);
      onSaved?.();
    } catch (e) { setForm(f => ({ ...f, status: previous })); toast.error('Transition failed'); }
  };

  const sendMessage = async () => {
    if (!msgInput.trim() || !eps) return;
    const body = { body: msgInput };
    setMsgInput('');
    try {
      const r = await api.post(eps.messages, body);
      setMessages(m => [...m, r.data?.message || { body: body.body, created_at: new Date().toISOString(), author: 'you' }]);
    } catch { toast.error('Send failed'); }
  };

  if (!entity) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto p-0" data-testid={`${entityType}-drawer`}>
        <SheetHeader className="px-5 py-3 border-b border-border sticky top-0 bg-background z-10">
          <SheetTitle className="flex items-center gap-2 text-base">
            <span className="font-mono text-xs text-muted-foreground">{entity.claim_number || entity.vo_number || entity.dispute_number || entity.reference_number || entity.id?.slice(0,8)}</span>
            <span className="text-foreground truncate">{entity.title || entity.milestone_name || entity.subject || '(untitled)'}</span>
            <Badge className={STATUS_COLORS[currentStatus] || 'bg-slate-500/20 text-slate-400'}>{currentStatus}</Badge>
          </SheetTitle>
          <SheetDescription className="text-xs">Editable {entityType} detail — status ribbon, fields, attachments and inline chat.</SheetDescription>
        </SheetHeader>

        {/* Status ribbon */}
        <div className="px-5 pt-3 pb-2 border-b border-border overflow-x-auto">
          <div className="flex items-center gap-1 min-w-max">
            {track.map((s, i) => {
              const active = s === currentStatus;
              const past = track.indexOf(currentStatus) > i
                && !TERMINAL_STATUSES.has(currentStatus) && !TERMINAL_STATUSES.has(s);
              return (
                <div key={s} className="flex items-center">
                  <button
                    onClick={() => transition(s)}
                    className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${active ? 'bg-purple-500/20 border-purple-500/60 text-purple-500' : past ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-500' : 'bg-muted border-border text-muted-foreground hover:text-foreground'}`}
                    data-testid={`status-${s}`}
                  >{s.replace(/_/g,' ')}</button>
                  {i < track.length - 1 && <div className="w-3 h-px bg-border" />}
                </div>
              );
            })}
          </div>
        </div>

        {/* Editable field grid */}
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            {fields.map(([k, label, type, opts]) => (
              <div key={k} className={type === 'textarea' ? 'space-y-1 col-span-2' : 'space-y-1'}>
                <Label className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</Label>
                {type === 'select' ? (
                  <Select value={form[k] ?? ''} onValueChange={v => setForm({ ...form, [k]: v })}>
                    <SelectTrigger className="bg-muted border-border h-9 text-sm"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{(opts || []).map(o => <SelectItem key={o} value={o}>{o.replace(/_/g,' ')}</SelectItem>)}</SelectContent>
                  </Select>
                ) : type === 'textarea' ? (
                  <Textarea rows={3} value={form[k] ?? ''} onChange={e => setForm({ ...form, [k]: e.target.value })} className="bg-muted border-border text-sm" data-testid={`field-${k}`} />
                ) : type === 'date' ? (
                  <Input type="date" value={dateVal(form[k])} onChange={e => setForm({ ...form, [k]: e.target.value })} className="bg-muted border-border h-9 text-sm" data-testid={`field-${k}`} />
                ) : (
                  <Input type={type === 'number' ? 'number' : 'text'} value={form[k] ?? ''} onChange={e => setForm({ ...form, [k]: e.target.value })} className="bg-muted border-border h-9 text-sm" data-testid={`field-${k}`} />
                )}
              </div>
            ))}
          </div>

          {/* Record detail the field grid does not cover — read-only, straight
              from the register row (delay events, evidence, rejection reason). */}
          <RecordDetail entityType={entityType} entity={entity} />

          {/* Existing supporting docs */}
          {Array.isArray(entity.supporting_docs) && entity.supporting_docs.filter(d => typeof d === 'object' && d.file_name).length > 0 && (
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-muted-foreground">Attached Documents</Label>
              <div className="mt-1 space-y-1">
                {entity.supporting_docs.filter(d => typeof d === 'object' && d.file_name).map((d, i) => (
                  <a key={i} href="#" onClick={(e) => { e.preventDefault(); openContractDoc(contractId, d.storage_path); }} className="flex items-center gap-2 text-xs rounded border border-border bg-muted px-2 py-1 hover:bg-muted/70">
                    <FileText className="w-3 h-3 text-blue-500 shrink-0" />
                    <span className="truncate text-foreground">{d.file_name}</span>
                    <ExternalLink className="w-3 h-3 text-muted-foreground ml-auto" />
                  </a>
                ))}
              </div>
            </div>
          )}
          <SupportingDocsPicker files={files} onChange={setFiles} testIdPrefix={`${entityType}-detail-docs`} />

          <div className="flex justify-end pt-2">
            <Button onClick={save} disabled={saving} className="bg-gradient-to-r from-[#1E4E8C] to-[#6B5CE7]" data-testid={`${entityType}-save`}>
              <Save className="w-4 h-4 mr-1.5" />{saving ? 'Saving…' : 'Save Changes'}
            </Button>
          </div>
        </div>

        <Separator />

        {/* Inline chat */}
        <div className="p-5" data-testid={`${entityType}-chat`}>
          <Label className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2 block">Activity · Comments</Label>
          <div className="space-y-2 max-h-64 overflow-y-auto mb-3">
            {messages.length === 0 && <p className="text-xs text-muted-foreground/70">No comments yet. Kick off the thread below.</p>}
            {messages.map((m, i) => (
              <div key={i} className="text-xs rounded-md border border-border bg-muted px-3 py-2">
                <div className="flex items-center gap-1.5 mb-1 text-muted-foreground">
                  <User className="w-3 h-3" /><span className="font-medium text-foreground">{m.author || 'user'}</span>
                  <Clock className="w-3 h-3 ml-1" /><span>{m.created_at ? new Date(m.created_at).toLocaleString() : '—'}</span>
                </div>
                <p className="text-foreground whitespace-pre-wrap">{m.body}</p>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Textarea rows={2} value={msgInput} onChange={e => setMsgInput(e.target.value)} placeholder="Add a comment…" className="bg-muted border-border text-sm" data-testid={`${entityType}-chat-input`} />
            <Button onClick={sendMessage} disabled={!msgInput.trim()} className="self-start" data-testid={`${entityType}-chat-send`}>
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function RecordDetail({ entityType, entity }) {
  const fmtDate = (v) => v ? new Date(v).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : '—';
  const delays = entityType === 'claim' ? (entity.delay_events || []) : [];
  const evidence = Array.isArray(entity.evidence) ? entity.evidence.filter(e => e && typeof e === 'object') : [];
  const reject = entity.rejection_reason;
  const basis = [
    ['Contract basis', entity.clause_reference || entity.supporting_clause],
    ['Raised by', entity.initiated_by || entity.raised_by],
    ['Approved days', entity.approved_days],
  ].filter(([, v]) => v !== null && v !== undefined && v !== '');

  if (!delays.length && !evidence.length && !reject && !basis.length) return null;

  return (
    <div className="space-y-3" data-testid={`${entityType}-record-detail`}>
      {basis.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          {basis.map(([label, v]) => (
            <div key={label}>
              <Label className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</Label>
              <p className="text-sm text-foreground">{String(v)}</p>
            </div>
          ))}
        </div>
      )}

      {reject && (
        <div className="rounded-md border border-red-500/40 bg-red-500/5 px-3 py-2" data-testid={`${entityType}-rejection-reason`}>
          <Label className="text-[11px] uppercase tracking-widest text-red-500">Rejection reason</Label>
          <p className="text-xs text-foreground mt-0.5">{reject}</p>
        </div>
      )}

      {delays.length > 0 && (
        <div data-testid="claim-delay-events">
          <Label className="text-[11px] uppercase tracking-widest text-muted-foreground">Delay events ({delays.length})</Label>
          <div className="mt-1 space-y-1">
            {delays.map((d, i) => (
              <div key={i} className="rounded border border-border bg-muted px-2 py-1.5 text-xs">
                <p className="text-foreground">{d.description}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  {fmtDate(d.start_date)} → {fmtDate(d.end_date)} · {d.days_lost ?? 0} days
                  {d.classification ? ` · ${String(d.classification).replace(/_/g, ' ').toLowerCase()}` : ''}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {evidence.length > 0 && (
        <div data-testid={`${entityType}-evidence`}>
          <Label className="text-[11px] uppercase tracking-widest text-muted-foreground">Evidence ({evidence.length})</Label>
          <div className="mt-1 space-y-1">
            {evidence.map((e, i) => (
              <div key={i} className="flex items-start gap-2 rounded border border-border bg-muted px-2 py-1.5 text-xs">
                <Paperclip className="w-3 h-3 text-blue-500 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <p className="text-foreground truncate">{e.reference || e.summary}</p>
                  <p className="text-[10px] text-muted-foreground">
                    {e.type ? `${String(e.type).replace(/_/g, ' ')} · ` : ''}{fmtDate(e.date)}{e.reference && e.summary ? ` · ${e.summary}` : ''}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default EntityDetailDrawer;